import { Injectable, inject } from '@angular/core'
import { Client, IMessage, StompSubscription } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { BehaviorSubject, Observable, Observer } from 'rxjs'
import { filter, first, switchMap } from 'rxjs/operators'
import { environment } from '../../environments/environment'
import Keycloak from 'keycloak-js'

// Enum برای نگهداری وضعیت اتصال
export enum SocketClientState {
	ATTEMPTING,
	CONNECTED,
	DISCONNECTED,
}

@Injectable({
	providedIn: 'root',
})
export class WebSocketStomp {
	private readonly keycloakService = inject(Keycloak)
	private client: Client
	private state = new BehaviorSubject<SocketClientState>(SocketClientState.DISCONNECTED)

	constructor() {
		this.client = new Client({
			// به جای WebSocket معمولی، از SockJS برای سازگاری بهتر استفاده می‌کنیم
			webSocketFactory: () => new SockJS(`${environment.wsUrl}`),
			debug: () => {
				// console.log(new Date(), str)
			}, // لاگ برای دیباگ کردن
			reconnectDelay: 5000, // تلاش مجدد برای اتصال هر ۵ ثانیه
			heartbeatIncoming: 4000,
			heartbeatOutgoing: 4000,
		})

		// مانیتور کردن وضعیت اتصال برای اطلاع‌رسانی به بقیه اپلیکیشن

		// Fired when a connection is successfully established
		this.client.onConnect = frame => {
			// console.log('✅ STOMP Connected:', frame)
			this.state.next(SocketClientState.CONNECTED)
		}

		// Fired when the client is disconnected, either purposefully or due to an error
		this.client.onDisconnect = frame => {
			// console.log('❌ STOMP Disconnected:', frame)
			this.state.next(SocketClientState.DISCONNECTED)
		}

		// Fired when the broker sends a STOMP ERROR frame
		this.client.onStompError = frame => {
			// console.error('🛑 STOMP Broker Error:', frame.headers['message'], 'Additional details:', frame.body)
			// Important: Update state on error
			this.state.next(SocketClientState.DISCONNECTED)
		}

		// Fired when the underlying WebSocket connection is closed for any reason
		this.client.onWebSocketClose = event => {
			// console.warn('🌐 WebSocket Closed:', event)
			// Important: Update state when the socket closes unexpectedly
			this.state.next(SocketClientState.DISCONNECTED)
		}

		// Fired on a WebSocket error
		this.client.onWebSocketError = event => {
			// console.error('🌐 WebSocket Error:', event)
		}
	}

	/**
	 * اتصال به سرور وب‌سوکت با استفاده از توکن JWT در هدر.
	 */
	connect(): void {
		if (this.state.value === SocketClientState.CONNECTED || this.state.value === SocketClientState.ATTEMPTING) {
			// console.log('WebSocket is already connected or attempting to connect.')
			return
		}

		this.state.next(SocketClientState.ATTEMPTING)

		// ✅ روش جدید و امن احراز هویت: ارسال توکن در هدر اتصال
		this.client.configure({
			connectHeaders: {
				Authorization: `Bearer ${this.keycloakService.token}`,
			},
		})

		this.client.activate()
	}

	/**
	 * قطع اتصال از سرور.
	 */
	disconnect(): void {
		if (this.client.active) {
			this.client.deactivate()
		}
	}

	/**
	 * اشتراک در یک "مقصد" (topic) خاص و دریافت پیام‌های آن به صورت Observable.
	 * این متد به صورت خودکار منتظر برقراری اتصال می‌ماند.
	 * @param topic مقصد مورد نظر (مثلاً /user/queue/notifications یا /topic/candles/1m/...)
	 */
	public subscribe<T>(topic: string): Observable<T> {
		return this.state.pipe(
			filter(state => state === SocketClientState.CONNECTED), // منتظر بمان تا اتصال برقرار شود
			first(), // فقط اولین سیگنال "متصل شد" را در نظر بگیر
			switchMap(() => {
				// یک Observable جدید برای این اشتراک خاص ایجاد کن
				return new Observable<T>((observer: Observer<T>) => {
					// console.log(`Subscribing to destination: ${topic}`)
					const subscription: StompSubscription = this.client.subscribe(topic, (message: IMessage) => {
						// پیام دریافتی را به عنوان JSON پارس کرده و به Observable ارسال کن
						const body = JSON.parse(message.body)
						observer.next(body as T)
					})

					// وقتی کامپوننت از این Observable خارج (unsubscribe) شد،
					// اشتراک از سرور STOMP را هم لغو کن
					return () => {
						// console.log(`Unsubscribing from destination: ${topic}`)
						subscription.unsubscribe()
					}
				})
			})
		)
	}

	public getState(): Observable<SocketClientState> {
		return this.state.asObservable()
	}
}
